import { useCallback, useEffect, useMemo, useState } from "react";
import { insertRow, listRows, updateRow } from "@/lib/adminApi";
import {
  ACTIVITY_STATUS,
  SAFETY_LEVEL,
  commerceMeta,
  commerceOptions,
  type Activity,
} from "@/lib/adminCommerce";
import {
  DataTable,
  DetailList,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  TextField,
  btnGhost,
  btnPrimary,
  fieldInput,
  textInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatRupees } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";

const EMPTY_ACTIVITY = {
  name: "",
  duration: "",
  safety_level: "Low",
  age_restriction: "",
  price: 0,
  capacity: 10,
  farm_compatibility: "",
  status: "active",
};

export default function AdminActivities() {
  const [rows, setRows] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [safetyFilter, setSafetyFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_ACTIVITY });
  const [saving, setSaving] = useState(false);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<Activity>("activities", "name"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load activities.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id: number, patch: Partial<Activity>, message?: string) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("activities", id, patch);
      if (message) notify(message);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const createActivity = async () => {
    if (!draft.name.trim()) {
      setError("An activity name is required.");
      return;
    }
    setSaving(true);
    try {
      const created = await insertRow<Activity>("activities", {
        ...draft,
        name: draft.name.trim(),
      });
      setRows((list) => [created, ...list]);
      setCreateOpen(false);
      setDraft({ ...EMPTY_ACTIVITY });
      notify(`${created.name} added to activities.`);
    } catch {
      setError("We couldn't create that activity. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (safetyFilter !== "all" && row.safety_level !== safetyFilter) return false;
      if (!query) return true;
      return [row.name, row.farm_compatibility || "", row.age_restriction || ""]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [rows, search, safetyFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;

  const columns: Column<Activity>[] = [
    {
      key: "name",
      header: "Activity",
      primary: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-secondary-100 text-secondary-700 shrink-0 flex items-center justify-center">
            <i className="ri-run-line"></i>
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.name}</p>
            <p className="text-xs text-foreground-500 mt-0.5">{row.farm_compatibility || "—"}</p>
          </div>
        </div>
      ),
    },
    {
      key: "duration",
      header: "Duration",
      render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.duration || "—"}</span>,
    },
    {
      key: "safety",
      header: "Safety",
      render: (row) => <StatusPill {...commerceMeta(SAFETY_LEVEL, row.safety_level)} />,
    },
    {
      key: "age",
      header: "Age",
      render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.age_restriction || "—"}</span>,
    },
    {
      key: "price",
      header: "Price",
      render: (row) => (
        <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{formatRupees(row.price)}</span>
      ),
    },
    { key: "capacity", header: "Capacity", render: (row) => <span className="text-sm text-foreground-700">{row.capacity}</span> },
    { key: "status", header: "Status", render: (row) => <StatusPill {...commerceMeta(ACTIVITY_STATUS, row.status)} /> },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Experiences"
        title="Activities"
        subtitle="Reusable building blocks — paddy harvesting, cattle feeding, bullock-cart rides — that compose into experiences and packages."
        actions={
          <button type="button" onClick={() => setCreateOpen(true)} className={btnPrimary}>
            <i className="ri-add-line"></i> New Activity
          </button>
        }
      />

      <StatCards
        stats={[
          { label: "Total activities", value: rows.length, icon: "ri-run-line", tone: "primary" },
          {
            label: "Active",
            value: rows.filter((row) => row.status === "active").length,
            icon: "ri-checkbox-circle-line",
            tone: "primary",
          },
          {
            label: "High safety level",
            value: rows.filter((row) => row.safety_level === "High").length,
            icon: "ri-alert-line",
            tone: "accent",
          },
          {
            label: "Avg. price",
            value: formatRupees(rows.length ? Math.round(rows.reduce((sum, row) => sum + Number(row.price || 0), 0) / rows.length) : 0),
            icon: "ri-price-tag-3-line",
            tone: "secondary",
          },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search activities…" />
          <Select
            label="Safety level"
            value={safetyFilter}
            onChange={setSafetyFilter}
            options={[{ value: "all", label: "All levels" }, ...commerceOptions(SAFETY_LEVEL)]}
          />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">{filtered.length} activities</span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading activities…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ri-run-line"
            title={rows.length === 0 ? "No activities yet" : "No activities match"}
            message="Reusable activities you define will appear here."
          />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.name || "Activity"}
        subtitle="Activity detail"
      >
        {selected ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill {...commerceMeta(SAFETY_LEVEL, selected.safety_level)} />
              <StatusPill {...commerceMeta(ACTIVITY_STATUS, selected.status)} />
            </div>

            <DetailList
              items={[
                { icon: "ri-time-line", label: "Duration", value: selected.duration || "—" },
                { icon: "ri-parent-line", label: "Age restriction", value: selected.age_restriction || "—" },
                { icon: "ri-price-tag-3-line", label: "Price", value: formatRupees(selected.price) },
                { icon: "ri-group-line", label: "Capacity", value: `${selected.capacity} guests` },
                { icon: "ri-plant-line", label: "Farm compatibility", value: selected.farm_compatibility || "—" },
              ]}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Safety level">
                <select
                  value={selected.safety_level || "Low"}
                  onChange={(event) => update(selected.id, { safety_level: event.target.value })}
                  className={fieldInput}
                >
                  {commerceOptions(SAFETY_LEVEL).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Status">
                <select
                  value={selected.status}
                  onChange={(event) => update(selected.id, { status: event.target.value }, "Activity status updated.")}
                  className={fieldInput}
                >
                  {commerceOptions(ACTIVITY_STATUS).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </>
        ) : null}
      </Drawer>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Changes save automatically.
      </p>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create a new activity"
        subtitle="New record"
        footer={
          <>
            <button type="button" onClick={() => setCreateOpen(false)} className={btnGhost}>
              Cancel
            </button>
            <button type="button" onClick={createActivity} disabled={saving} className={btnPrimary}>
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
              Create activity
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <TextField label="Activity name">
              <input
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                placeholder="e.g. Paddy Harvesting"
                className={textInput}
              />
            </TextField>
          </div>
          <TextField label="Duration">
            <input
              value={draft.duration}
              onChange={(event) => setDraft({ ...draft, duration: event.target.value })}
              placeholder="e.g. 1 hour"
              className={textInput}
            />
          </TextField>
          <TextField label="Safety level">
            <select
              value={draft.safety_level}
              onChange={(event) => setDraft({ ...draft, safety_level: event.target.value })}
              className={fieldInput}
            >
              {commerceOptions(SAFETY_LEVEL).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </TextField>
          <TextField label="Age restriction">
            <input
              value={draft.age_restriction}
              onChange={(event) => setDraft({ ...draft, age_restriction: event.target.value })}
              placeholder="e.g. 6+ years"
              className={textInput}
            />
          </TextField>
          <TextField label="Price (₹)">
            <input
              type="number"
              value={draft.price}
              onChange={(event) => setDraft({ ...draft, price: Number(event.target.value) })}
              className={textInput}
            />
          </TextField>
          <TextField label="Capacity (guests)">
            <input
              type="number"
              value={draft.capacity}
              onChange={(event) => setDraft({ ...draft, capacity: Number(event.target.value) })}
              className={textInput}
            />
          </TextField>
          <TextField label="Status">
            <select
              value={draft.status}
              onChange={(event) => setDraft({ ...draft, status: event.target.value })}
              className={fieldInput}
            >
              {commerceOptions(ACTIVITY_STATUS).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </TextField>
          <div className="sm:col-span-2">
            <TextField label="Farm compatibility">
              <input
                value={draft.farm_compatibility}
                onChange={(event) => setDraft({ ...draft, farm_compatibility: event.target.value })}
                placeholder="e.g. Paddy farms, Coconut farms"
                className={textInput}
              />
            </TextField>
          </div>
        </div>
      </Modal>
      {node}
    </div>
  );
}